// Connection state for the native shell.
//
// The app has no offline data path: when connectivity drops or the backend
// cold-starts, staff must see a clear "reconnecting" state rather than a silent
// hang. This module is display-only — it queues nothing and mutates nothing.
//
// The reducer and health ping live in connection/probe.js so this provider and
// the browser surface's SyncProvider run identical logic rather than two copies
// that drift. Signals: navigator.onLine plus the online/offline events drive
// `online`; a periodic GET /health drives `reconnecting`, paused while the
// document is hidden and re-checked on focus and on regained connectivity.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import api from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import {
    connectionReducer, initialConnectionState, pingHealth, navigatorOnline,
    HEALTH_PATH, DEFAULT_PING_INTERVAL_MS,
} from '../connection/probe';

export { HEALTH_PATH, DEFAULT_PING_INTERVAL_MS, connectionReducer };

const ConnectionContext = createContext(null);

export function ConnectionProvider({ children, pingIntervalMs = DEFAULT_PING_INTERVAL_MS }) {
    const [state, dispatch] = useReducer(connectionReducer, undefined, initialConnectionState);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    // The ping itself never throws — failures are a normal signal, not an error.
    const ping = useCallback(async () => {
        const ok = await pingHealth(api);
        if (!mountedRef.current) return;
        dispatch({ type: ok ? 'PING_SUCCESS' : 'PING_FAILURE' });
    }, []);

    // ---- navigator online/offline --------------------------------------
    useEffect(() => {
        const handleOnline = () => {
            dispatch({ type: 'NAVIGATOR_ONLINE' });
            ping(); // confirm the backend is actually reachable, not just the NIC
        };
        const handleOffline = () => dispatch({ type: 'NAVIGATOR_OFFLINE' });
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, [ping]);

    // ---- immediate re-check on window focus ----------------------------
    useEffect(() => {
        const handleFocus = () => {
            if (navigatorOnline()) ping();
        };
        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, [ping]);

    // ---- periodic health ping, paused while the tab/app is hidden ------
    useEffect(() => {
        let intervalId = null;

        const tick = () => {
            if (typeof document !== 'undefined' && document.hidden) return;
            if (!navigatorOnline()) return;
            ping();
        };

        const start = () => {
            if (intervalId != null) return;
            intervalId = setInterval(tick, pingIntervalMs);
        };
        const stop = () => {
            if (intervalId == null) return;
            clearInterval(intervalId);
            intervalId = null;
        };

        // Initial check on mount — surfaces a cold-start backend immediately
        // rather than waiting a full interval.
        ping();
        start();

        const handleVisibility = () => {
            if (typeof document === 'undefined') return;
            if (document.hidden) {
                stop();
            } else {
                start();
                if (navigatorOnline()) ping();
            }
        };
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', handleVisibility);
        }

        return () => {
            stop();
            if (typeof document !== 'undefined') {
                document.removeEventListener('visibilitychange', handleVisibility);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pingIntervalMs, ping]);

    const value = useMemo(
        () => ({ online: state.online, reconnecting: state.reconnecting, retry: ping }),
        [state.online, state.reconnecting, ping]
    );

    return <ConnectionContext.Provider value={value}>{children}</ConnectionContext.Provider>;
}

// Resilient default (mirrors other pos-app hooks' provider-optional pattern)
// so a station never crashes if it's ever rendered without the provider —
// it just reports "online" and does nothing on retry().
const FALLBACK = { online: true, reconnecting: false, retry: () => {} };

export function useConnection() {
    const ctx = useContext(ConnectionContext);
    return ctx || FALLBACK;
}

// ---------------------------------------------------------------------
// ConnectionBanner — subtle, non-blocking banner for Cashier/Waiter. KDS
// keeps its own top-bar pill (see KdsStation.js) since it has no room for a
// second chrome row and the pill already communicates the same signal.
// Renders nothing while healthy; appears as a slim glass strip under the
// StationTopBar with a Retry affordance (forces an immediate ping).
const BANNER_CSS = `
.cnx-banner{display:flex;align-items:center;gap:10px;flex:none;box-sizing:border-box;
  padding:9px 14px;border-radius:14px;font-size:12.5px;font-weight:700;
  animation:cnxIn .18s ease-out;}
.cnx-banner.cnx-offline{color:var(--c-red);background:var(--c-redLight);border:1px solid var(--c-redBorder);}
.cnx-banner.cnx-reconnecting{color:var(--c-amber);background:var(--c-amberLight);border:1px solid var(--c-amberBorder);}
.cnx-dot{width:8px;height:8px;border-radius:50%;flex:none;background:currentColor;
  animation:cnxBlink 1s ease-in-out infinite;}
.cnx-text{flex:1;min-width:0;}
.cnx-retry{flex:none;min-height:32px;padding:0 14px;border-radius:999px;cursor:pointer;
  border:1px solid currentColor;background:transparent;color:inherit;
  font-size:12px;font-weight:800;font-family:inherit;
  transition:transform .15s ease, background .15s ease;}
.cnx-retry:active{transform:scale(.95);}
@keyframes cnxIn{from{opacity:0;transform:translateY(-4px);}to{opacity:1;transform:translateY(0);}}
@keyframes cnxBlink{50%{opacity:.35;}}
@media (prefers-reduced-motion: reduce){.cnx-dot{animation:none !important;}.cnx-banner{animation:none !important;}}
`;

export function ConnectionBanner() {
    const { t } = useLanguage();
    const { online, reconnecting, retry } = useConnection();
    const [retrying, setRetrying] = React.useState(false);

    if (online && !reconnecting) return null;

    const state = !online ? 'offline' : 'reconnecting';
    const message = state === 'offline' ? t('offline') : t('reconnecting');

    const handleRetry = async () => {
        setRetrying(true);
        try {
            await retry();
        } finally {
            setRetrying(false);
        }
    };

    return (
        <div className={`cnx-banner cnx-${state}`} role="status" aria-live="polite">
            <style>{BANNER_CSS}</style>
            <span className="cnx-dot" aria-hidden="true" />
            <span className="cnx-text">{message}</span>
            <button type="button" className="cnx-retry" onClick={handleRetry} disabled={retrying}>
                {t('retry')}
            </button>
        </div>
    );
}

export default ConnectionContext;
